<template>
  <a-modal :visible="visible" title="退货（F4）" :maskClosable="false" width="720px"
           :footer="null" @cancel="$emit('cancel')">
    <!-- 查原单 -->
    <div class="return-search">
      <a-input-search v-model="keyword" placeholder="输入小票号/单号查询（留空显示最近 10 单）"
                      enterButton="查询" style="flex: 1" @search="loadOrders" />
    </div>
    <!-- 原单列表 -->
    <div v-if="!currentOrder" class="order-list">
      <div v-if="!orders.length" class="empty-tip">暂无符合条件的收银单</div>
      <div v-for="order in orders" :key="order.id" class="order-card" @click="pickOrder(order)">
        <div class="order-line">
          <span class="order-no">{{ order.number }}</span>
          <span class="order-member">{{ order.memberName || '散客' }}</span>
          <span class="order-amount">¥{{ format(order.totalPrice) }}</span>
        </div>
        <div class="order-line sub">
          <span>{{ order.operTime }}</span>
          <span>支付：{{ order.payType }}</span>
          <span v-if="order.refundBlocked" class="blocked">储值支付 · 不可在此退货</span>
        </div>
      </div>
    </div>
    <!-- 明细勾选 -->
    <div v-else>
      <div class="picked-head">
        <span>{{ currentOrder.number }} · {{ currentOrder.memberName || '散客' }} · ¥{{ format(currentOrder.totalPrice) }}</span>
        <a @click="backToList">返回重选</a>
      </div>
      <div class="item-list">
        <div v-for="item in currentOrder.items" :key="item.itemId" class="item-row">
          <a-checkbox :checked="isSelected(item.itemId)" :disabled="!item.returnable || Number(item.returnable) <= 0"
                      @change="e => toggleItem(item, e.target.checked)" />
          <span class="item-name">{{ item.materialName }}（{{ item.unit }}）</span>
          <span class="item-info">原 {{ item.operNumber }} · 已退 {{ item.returnedNumber }}</span>
          <span class="item-price">¥{{ format(item.unitPrice) }}</span>
          <div class="stepper" v-if="isSelected(item.itemId)">
            <a class="step-btn" @click="changeQty(item, -1)">−</a>
            <input class="step-input" :value="qtyMap[item.itemId]" inputmode="decimal"
                   @change="e => onQtyInput(item, e)" />
            <a class="step-btn" @click="changeQty(item, 1)">＋</a>
          </div>
          <span class="item-returnable" v-else>可退 {{ item.returnable }}</span>
        </div>
      </div>
      <div class="return-footer">
        <span>退货合计：<span class="return-total">¥{{ format(returnTotal) }}</span></span>
        <a-button type="danger" :loading="settling" :disabled="!selectedItems.length" @click="confirmReturn">
          确认退货
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
  import { cashierReturnOrder, cashierReturnSettle } from '@/api/plugin/member'

  /**
   * 退货弹窗：查原单 → 勾选商品+数量 → 生成零售退货单（LSTH，已审核回库存）
   * P0 边界：积分/储值不自动回退；储值支付单据拦截
   */
  export default {
    name: 'ReturnModal',
    props: {
      visible: { type: Boolean, default: false }
    },
    data() {
      return {
        keyword: '',
        orders: [],
        currentOrder: null,
        qtyMap: {},
        settling: false
      }
    },
    computed: {
      selectedItems() {
        if (!this.currentOrder) return []
        return this.currentOrder.items.filter(i => this.isSelected(i.itemId))
      },
      returnTotal() {
        return this.selectedItems.reduce((sum, i) =>
          sum + Number(i.unitPrice || 0) * Number(this.qtyMap[i.itemId] || 0), 0)
      }
    },
    watch: {
      visible(val) {
        if (val) {
          this.keyword = ''
          this.currentOrder = null
          this.qtyMap = {}
          this.loadOrders()
        }
      }
    },
    methods: {
      format(price) {
        return Number(price || 0).toFixed(2)
      },
      loadOrders() {
        cashierReturnOrder(this.keyword || '').then(res => {
          if (res && res.code === 200) {
            this.orders = res.data || []
          } else {
            this.$message.warning((res && res.message) || '查询失败')
          }
        })
      },
      pickOrder(order) {
        if (order.refundBlocked) {
          this.$message.warning('储值支付订单请走会员储值退款流程')
          return
        }
        this.currentOrder = order
        this.qtyMap = {}
      },
      backToList() {
        this.currentOrder = null
      },
      isSelected(itemId) {
        return this.qtyMap[itemId] !== undefined
      },
      toggleItem(item, checked) {
        if (checked) {
          this.$set(this.qtyMap, item.itemId, Number(item.returnable))
        } else {
          this.$delete(this.qtyMap, item.itemId)
        }
      },
      changeQty(item, delta) {
        const max = Number(item.returnable)
        const next = Number(this.qtyMap[item.itemId] || 0) + delta
        if (next <= 0) {
          this.$delete(this.qtyMap, item.itemId)
          return
        }
        this.$set(this.qtyMap, item.itemId, Math.min(next, max))
      },
      onQtyInput(item, e) {
        const value = parseFloat(e.target.value)
        if (!isNaN(value) && value > 0) {
          this.$set(this.qtyMap, item.itemId, Math.min(value, Number(item.returnable)))
        } else {
          e.target.value = this.qtyMap[item.itemId]
        }
      },
      confirmReturn() {
        const items = this.selectedItems.map(i => ({
          itemId: i.itemId,
          operNumber: this.qtyMap[i.itemId]
        }))
        this.settling = true
        cashierReturnSettle({ headId: this.currentOrder.id, items }).then(res => {
          if (res && res.code === 200) {
            const data = res.data || {}
            this.$message.success(`退货单 ${data.billNo} 已生成（¥${this.format(data.total)}），如涉及积分请人工处理`)
            this.$emit('done')
            this.$emit('cancel')
          } else {
            this.$message.warning((res && res.message) || '退货失败')
          }
        }).finally(() => {
          this.settling = false
        })
      }
    }
  }
</script>

<style scoped>
  .return-search {
    display: flex;
    margin-bottom: 12px;
  }
  .order-list {
    max-height: 420px;
    overflow-y: auto;
  }
  .order-card {
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 8px;
    cursor: pointer;
  }
  .order-card:hover {
    border-color: #0081ff;
  }
  .order-line {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .order-line.sub {
    color: #999;
    font-size: 12px;
    margin-top: 4px;
  }
  .order-no {
    font-weight: 600;
    color: #333;
  }
  .order-member {
    flex: 1;
    color: #555;
  }
  .order-amount {
    color: #e4393c;
    font-weight: 600;
  }
  .blocked {
    color: #faad14;
  }
  .empty-tip {
    text-align: center;
    color: #bbb;
    padding: 40px 0;
  }
  .picked-head {
    display: flex;
    justify-content: space-between;
    color: #555;
    margin-bottom: 10px;
  }
  .item-list {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid #f0f0f0;
    border-radius: 6px;
    padding: 4px 10px;
  }
  .item-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px dashed #f0f0f0;
    font-size: 13px;
  }
  .item-row:last-child {
    border-bottom: none;
  }
  .item-name {
    width: 180px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .item-info {
    flex: 1;
    color: #999;
    font-size: 12px;
  }
  .item-price {
    color: #555;
  }
  .item-returnable {
    color: #0081ff;
    font-size: 12px;
    width: 110px;
    text-align: right;
  }
  .stepper {
    display: flex;
    align-items: center;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    overflow: hidden;
  }
  .step-btn {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    color: #555;
    cursor: pointer;
    user-select: none;
  }
  .step-input {
    width: 46px;
    height: 24px;
    border: none;
    border-left: 1px solid #e8e8e8;
    border-right: 1px solid #e8e8e8;
    text-align: center;
    outline: none;
  }
  .return-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
  }
  .return-total {
    color: #e4393c;
    font-size: 20px;
    font-weight: 700;
  }
</style>
